/**
 * Resolve what Copy/Cut should put on the clipboard for selected events.
 *
 * Preference order:
 * 1. A non-empty range in the target Quill editor
 * 2. Any highlighted DOM text (original preview, original Quill, table cells)
 * 3. JSON of the selected events (or concatenated event.text when fallback is "eventText")
 */

/**
 * Adjacent HTML `<p>` blocks serialize as blank lines in selection.toString().
 * Caption lines should copy with a single newline, matching Quill getText().
 * @param {string} text
 * @returns {string}
 */
function normalizeCopiedDomText(text) {
    return text.replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n");
}

/**
 * Visible text currently highlighted in the document, or an empty string.
 * @returns {string}
 */
export function getHighlightedDomText() {
    if (
        typeof globalThis === "undefined" ||
        typeof globalThis.getSelection !== "function"
    ) {
        return "";
    }

    const selection = globalThis.getSelection();
    if (!selection || selection.isCollapsed) {
        return "";
    }

    return normalizeCopiedDomText(selection.toString());
}

/**
 * @param {object} [eventGroup]
 * @param {object} [quill]
 * @returns {{ editor: object, range: { index: number, length: number }, text: string } | null}
 */
export function getQuillTextSelection(eventGroup, quill) {
    const selected = eventGroup?.selected;
    if (!Array.isArray(selected) || selected.length !== 1) {
        return null;
    }

    const editor =
        quill ??
        (typeof globalThis !== "undefined" ? globalThis.quillEditor : undefined);
    const range = editor?.getSelection?.();
    if (!range || range.length === 0) {
        return null;
    }

    return {
        editor,
        range,
        text: editor.getText(range.index, range.length),
    };
}

/**
 * @param {{ eventGroup?: object, quill?: object, fallback?: "json" | "eventText" }} [options]
 * @returns {string | null}
 */
export function getCopyText({
    eventGroup,
    quill,
    fallback = "json",
} = {}) {
    const selected = eventGroup?.selected;
    if (!Array.isArray(selected) || selected.length === 0) {
        return null;
    }

    const quillSelection = getQuillTextSelection(eventGroup, quill);
    if (quillSelection) {
        return quillSelection.text;
    }

    const domText = getHighlightedDomText();
    if (domText) {
        return domText;
    }

    const orderedIndexes = [...selected].sort((a, b) => a - b);
    const eventsToCopy = orderedIndexes.map(
        (index) => eventGroup.events[index],
    );

    if (fallback === "eventText") {
        return eventsToCopy.map((event) => event?.text || "").join("");
    }

    return JSON.stringify(eventsToCopy);
}
